import { useCallback, useEffect, useState } from 'react'
import { useQuery } from 'react-query'

import { ChartTypeV2 as ChartType } from 'features/charts-v2/types/chart-types'
import type { TrendDetectionService } from 'features/trend-detection/service/service'
import type {
  Chart,
  IssueCardData,
  PostText,
} from 'features/trend-detection/types'
import type { IssueResponse } from 'features/trend-detection/types'
import {
  DEFAULT_BREAKDOWN,
  parseBreakdownSelection,
} from 'features/trend-detection/utils/breakdown-utils'
import type { IssueBreakdownSelectionValue } from 'features/trend-detection/utils/dimension-mappings'
import { mapIssuesToCardData } from 'features/trend-detection/utils/helpers'

export interface DetailDataConfig<T> {
  queryKey: string
  fetchFn: (projectId: string, uuid: string) => Promise<T>
  /** Extract the issue UUID used for charts/breakdowns/verbatims. */
  getIssueUuid: (item: T) => string | null
  /** Extract the run UUID from the fetched item. */
  getRunUuid: (item: T) => string | null
  /** Extract the IssueResponse used to derive IssueCardData. */
  getIssue: (item: T) => IssueResponse | null
}

export interface DetailDataResult<T> {
  item: T | null
  cardData: IssueCardData | null
  loading: boolean
  error: boolean

  timeseriesChart: Chart | undefined
  projectTotalChart: Chart | undefined
  timeseriesLoading: boolean

  breakdownChart: Chart | null | undefined
  breakdownLoading: boolean
  selectedBreakdownDimension: IssueBreakdownSelectionValue
  breakdownChartType: ChartType
  onBreakdownDimensionChange: (dim: IssueBreakdownSelectionValue) => void
  onBreakdownChartTypeChange: (type: ChartType) => void
  refetchBreakdowns: (dim?: IssueBreakdownSelectionValue) => void

  postTexts: PostText[]
  verbatimsLoading: boolean
}

export function useDetailData<T>(
  service: TrendDetectionService,
  projectId: string | null,
  uuid: string | null,
  config: DetailDataConfig<T>,
): DetailDataResult<T> {
  // 1. Fetch detail
  const {
    data: item,
    isLoading: loading,
    isError: error,
  } = useQuery<T>(
    [config.queryKey, projectId, uuid],
    () => config.fetchFn(projectId!, uuid!),
    {
      enabled: !!projectId && !!uuid,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  )

  const runUuid = item ? config.getRunUuid(item) : null
  const issueUuid = item ? config.getIssueUuid(item) : null

  const cardData: IssueCardData | null = (() => {
    if (!item) return null
    const issue = config.getIssue(item)
    if (!issue) return null
    const cards = mapIssuesToCardData([issue])
    return cards[0] ?? null
  })()

  // 2. Timeseries chart
  const [timeseriesChart, setTimeseriesChart] = useState<Chart | undefined>(
    undefined,
  )
  const [projectTotalChart, setProjectTotalChart] = useState<Chart | undefined>(
    undefined,
  )
  const [timeseriesLoading, setTimeseriesLoading] = useState(false)

  useEffect(() => {
    if (!projectId || !runUuid || !issueUuid) return
    setTimeseriesLoading(true)
    service
      .fetchIssueTimeseriesChart(projectId, runUuid, issueUuid)
      .then((response) => {
        setTimeseriesChart(response.issue_chart)
        setProjectTotalChart(response.project_total_chart)
      })
      .catch(() => {})
      .finally(() => setTimeseriesLoading(false))
  }, [projectId, runUuid, issueUuid, service])

  // 3. Breakdown chart
  const [breakdownChart, setBreakdownChart] = useState<
    Chart | null | undefined
  >(undefined)
  const [breakdownLoading, setBreakdownLoading] = useState(false)
  const [selectedBreakdownDimension, setSelectedBreakdownDimension] =
    useState<IssueBreakdownSelectionValue>(DEFAULT_BREAKDOWN)
  const [breakdownChartType, setBreakdownChartType] = useState<ChartType>(
    ChartType.PIE,
  )

  const fetchBreakdowns = useCallback(
    (dim?: IssueBreakdownSelectionValue) => {
      if (!projectId || !runUuid || !issueUuid) return
      const selectedDim = dim ?? selectedBreakdownDimension
      const { dimension, customField } = parseBreakdownSelection(selectedDim)
      setBreakdownLoading(true)
      service
        .fetchIssueBreakdowns(
          projectId,
          runUuid,
          issueUuid,
          dimension,
          customField,
        )
        .then((res) => setBreakdownChart(res.breakdown_chart))
        .catch(() => setBreakdownChart(null))
        .finally(() => setBreakdownLoading(false))
    },
    [projectId, runUuid, issueUuid, selectedBreakdownDimension, service],
  )

  useEffect(() => {
    if (!projectId || !runUuid || !issueUuid) return
    fetchBreakdowns()
    // Only fire on initial data load, not when fetchBreakdowns reference changes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit fetchBreakdowns to avoid refetch loops
  }, [projectId, runUuid, issueUuid])

  const onBreakdownDimensionChange = useCallback(
    (dim: IssueBreakdownSelectionValue) => {
      setSelectedBreakdownDimension(dim)
      fetchBreakdowns(dim)
    },
    [fetchBreakdowns],
  )

  const onBreakdownChartTypeChange = useCallback((type: ChartType) => {
    setBreakdownChartType(type)
  }, [])

  // 4. Verbatims
  const [postTexts, setPostTexts] = useState<PostText[]>([])
  const [verbatimsLoading, setVerbatimsLoading] = useState(false)

  useEffect(() => {
    if (!projectId || !runUuid || !issueUuid) return
    setVerbatimsLoading(true)
    service
      .fetchIssuePostTexts(projectId, runUuid, issueUuid)
      .then((res) => setPostTexts(res.post_texts))
      .catch(() => {})
      .finally(() => setVerbatimsLoading(false))
  }, [projectId, runUuid, issueUuid, service])

  return {
    item: item ?? null,
    cardData,
    loading,
    error,
    timeseriesChart,
    projectTotalChart,
    timeseriesLoading,
    breakdownChart,
    breakdownLoading,
    selectedBreakdownDimension,
    breakdownChartType,
    onBreakdownDimensionChange,
    onBreakdownChartTypeChange,
    refetchBreakdowns: fetchBreakdowns,
    postTexts,
    verbatimsLoading,
  }
}
